import { createFileRoute, Link, Navigate, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { InputType, SessionSummary } from "@re/shared";
import { api } from "../lib/api";
import {
  Badge,
  Button,
  Card,
  Icon,
  Input,
  SourceBadge,
  SourceIcon,
  VisibilityPill,
  cn,
  fmtRelative,
} from "../components/ui";

export const Route = createFileRoute("/me")({ component: MePage });

const SOURCE_TYPES: InputType[] = [
  "youtube",
  "arxiv",
  "github",
  "huggingface",
  "blog",
  "community",
  "topic",
];

function MePage() {
  const { t } = useTranslation();
  const me = useQuery({ queryKey: ["me"], queryFn: () => api.me(), retry: false });
  if (me.isLoading) return <p className="py-16 text-center text-ink-faint">{t("common.loading")}</p>;
  if (me.isError || !me.data) return <Navigate to="/login" />;
  return <MySessions name={me.data.name} />;
}

function MySessions({ name }: { name: string }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const mine = useQuery({ queryKey: ["sessions", "mine"], queryFn: () => api.listMySessions() });
  const del = useMutation({
    mutationFn: (slug: string) => api.deleteSession(slug),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["sessions", "mine"] });
      qc.invalidateQueries({ queryKey: ["sessions", "public"] });
    },
  });

  const [q, setQ] = useState("");
  const [vis, setVis] = useState<"all" | "public" | "private">("all");
  const [type, setType] = useState<InputType | "all">("all");
  const [view, setView] = useState<"list" | "grid">("list");

  const all = mine.data ?? [];
  const counts = {
    all: all.length,
    public: all.filter((s) => s.visibility === "public").length,
    private: all.filter((s) => s.visibility === "private").length,
  };

  const filtered = useMemo(
    () =>
      all.filter((s) => {
        const needle = q.trim().toLowerCase();
        if (
          needle &&
          !s.title.toLowerCase().includes(needle) &&
          !(s.intent_purpose ?? "").toLowerCase().includes(needle)
        )
          return false;
        if (vis !== "all" && s.visibility !== vis) return false;
        if (type !== "all" && s.input_type !== type) return false;
        return true;
      }),
    [all, q, vis, type],
  );

  const onDelete = (s: SessionSummary) => {
    if (window.confirm(t("me.deleteConfirm", { title: s.title }))) del.mutate(s.slug);
  };

  return (
    <main className="max-w-[1320px] mx-auto px-6 py-10">
      <div className="flex items-end justify-between gap-6 mb-7 flex-wrap">
        <div>
          <div
            className="text-micro uppercase tracking-wider font-medium text-ink-faint mb-2"
            style={{ letterSpacing: "0.08em" }}
          >
            {t("me.eyebrow", { name })}
          </div>
          <h1 className="font-serif text-[36px] leading-[1.1] font-medium text-ink tracking-tight">
            {t("me.title")}{" "}
            <span className="text-ink-faint font-mono text-[20px] align-baseline">({counts.all})</span>
          </h1>
        </div>
        <Button variant="accent" size="lg" icon="plus" onClick={() => navigate({ to: "/new" })}>
          {t("me.newResearch")}
        </Button>
      </div>

      {/* ─── Filter bar ─── */}
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <div className="flex-1 min-w-[260px] max-w-[440px]">
          <Input
            icon="search"
            size="md"
            placeholder={t("me.searchPlaceholder")}
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        <div className="inline-flex items-center bg-surface-2 border border-border rounded-md p-1 gap-1">
          {(
            [
              { id: "all", label: t("me.filterAll", { count: counts.all }) },
              { id: "private", label: t("me.filterPrivate", { count: counts.private }) },
              { id: "public", label: t("me.filterPublic", { count: counts.public }) },
            ] as const
          ).map((f) => (
            <button
              key={f.id}
              onClick={() => setVis(f.id)}
              className={cn(
                "h-7 px-3 rounded text-[12.5px] font-medium transition-colors",
                vis === f.id ? "bg-surface text-ink shadow-sm" : "text-ink-faint hover:text-ink-dim",
              )}
            >
              {f.label}
            </button>
          ))}
        </div>
        <select
          value={type}
          onChange={(e) => setType(e.target.value as InputType | "all")}
          className="h-9 px-3 bg-surface border border-border rounded-md text-[13px] text-ink-dim"
        >
          <option value="all">{t("me.allSources")}</option>
          {SOURCE_TYPES.map((tp) => (
            <option key={tp} value={tp}>
              {t(`source.${tp}`)}
            </option>
          ))}
        </select>
        <div className="flex-1" />
        <div className="inline-flex items-center bg-surface-2 border border-border rounded-md p-1 gap-1">
          <button
            onClick={() => setView("list")}
            aria-label={t("me.listView")}
            className={cn(
              "w-7 h-7 rounded inline-flex items-center justify-center transition-colors",
              view === "list" ? "bg-surface text-ink shadow-sm" : "text-ink-faint hover:text-ink-dim",
            )}
          >
            <Icon name="list" size={14} />
          </button>
          <button
            onClick={() => setView("grid")}
            aria-label={t("me.gridView")}
            className={cn(
              "w-7 h-7 rounded inline-flex items-center justify-center transition-colors",
              view === "grid" ? "bg-surface text-ink shadow-sm" : "text-ink-faint hover:text-ink-dim",
            )}
          >
            <Icon name="menu" size={14} />
          </button>
        </div>
      </div>

      {/* ─── Sessions ─── */}
      {mine.isLoading ? (
        <p className="text-ink-faint">{t("common.loading")}</p>
      ) : mine.error ? (
        <p className="text-[color:var(--danger)]">{t("me.loadFailed", { error: String(mine.error) })}</p>
      ) : all.length === 0 ? (
        <div className="border border-border border-dashed rounded-lg py-20 text-center">
          <div className="text-ink-faint mb-3">
            <Icon name="book" size={28} className="mx-auto opacity-50" />
          </div>
          <div className="text-[14px] text-ink-dim mb-4">{t("me.empty")}</div>
          <Button variant="accent" size="sm" icon="plus" onClick={() => navigate({ to: "/new" })}>
            {t("me.emptyCta")}
          </Button>
        </div>
      ) : filtered.length === 0 ? (
        <div className="border border-border border-dashed rounded-lg py-20 text-center">
          <div className="text-ink-faint mb-3">
            <Icon name="search" size={28} className="mx-auto opacity-50" />
          </div>
          <div className="text-[14px] text-ink-dim">{t("me.emptyFiltered")}</div>
        </div>
      ) : view === "list" ? (
        <div className="border border-border rounded-lg bg-surface overflow-hidden divide-y divide-border">
          {filtered.map((s) => (
            <SessionRow
              key={s.slug}
              s={s}
              lang={i18n.language}
              pending={del.isPending}
              onDelete={() => onDelete(s)}
            />
          ))}
        </div>
      ) : (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map((s) => (
            <PrivateSessionCard
              key={s.slug}
              s={s}
              lang={i18n.language}
              pending={del.isPending}
              onDelete={() => onDelete(s)}
            />
          ))}
        </div>
      )}
    </main>
  );
}

function statusBadge(status: NonNullable<SessionSummary["status"]>, label: string) {
  const tone = status === "error" ? "danger" : "warn";
  return (
    <Badge tone={tone} size="xs">
      {label}
    </Badge>
  );
}

function SessionRow({
  s,
  lang,
  pending,
  onDelete,
}: {
  s: SessionSummary;
  lang: string;
  pending: boolean;
  onDelete: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="group flex items-center gap-5 px-5 py-4 hover:bg-surface-2 transition-colors">
      <Link
        to="/sessions/$slug"
        params={{ slug: s.slug }}
        className="flex items-center gap-5 flex-1 min-w-0"
      >
        <div className="w-7 h-7 rounded-md bg-surface-2 border border-border flex items-center justify-center text-ink-dim shrink-0">
          <SourceIcon type={s.input_type} size={14} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2.5 mb-1">
            <div className="font-serif text-[16px] font-medium text-ink leading-tight truncate">
              {s.title}
            </div>
            {s.visibility && <VisibilityPill visibility={s.visibility} size="xs" />}
            {s.status &&
              s.status !== "done" &&
              statusBadge(s.status, t(s.status === "error" ? "job.statusError" : "job.statusRunning"))}
          </div>
          <div className="text-[12px] text-ink-faint font-mono truncate">{s.input}</div>
        </div>
      </Link>
      <div className="hidden md:flex items-center gap-5 text-[12px] text-ink-faint font-mono shrink-0">
        <span>{t("landing.sourceCount", { count: s.source_count })}</span>
        {s.model && <span>{s.model}</span>}
        <span className="w-16 text-right">{fmtRelative(s.created, lang)}</span>
      </div>
      <button
        onClick={onDelete}
        disabled={pending}
        aria-label={t("me.delete")}
        className="shrink-0 opacity-100 md:opacity-0 md:group-hover:opacity-100 md:focus:opacity-100 transition-opacity w-8 h-8 rounded-md hover:bg-danger-soft inline-flex items-center justify-center text-ink-faint hover:text-[color:var(--danger)] disabled:opacity-40"
      >
        <Icon name="trash" size={14} />
      </button>
    </div>
  );
}

function PrivateSessionCard({
  s,
  lang,
  pending,
  onDelete,
}: {
  s: SessionSummary;
  lang: string;
  pending: boolean;
  onDelete: () => void;
}) {
  const { t } = useTranslation();
  return (
    <Card className="relative p-5 group">
      <Link to="/sessions/$slug" params={{ slug: s.slug }} className="block">
        <div className="flex items-center gap-2 mb-3">
          <SourceBadge type={s.input_type} />
          {s.visibility && <VisibilityPill visibility={s.visibility} size="xs" />}
          {s.status &&
            s.status !== "done" &&
            statusBadge(s.status, t(s.status === "error" ? "job.statusError" : "job.statusRunning"))}
          <div className="flex-1" />
          <div className="text-[12px] text-ink-faint font-mono">{fmtRelative(s.created, lang)}</div>
        </div>
        <h3 className="font-serif text-[17px] leading-[1.25] font-medium text-ink tracking-tight mb-1.5 group-hover:text-accent-ink transition-colors">
          {s.title}
        </h3>
        {s.intent_purpose && (
          <p className="text-[13px] text-ink-dim line-clamp-2 leading-snug mb-3">{s.intent_purpose}</p>
        )}
      </Link>
      <div className="flex items-center gap-3 text-[11.5px] text-ink-faint font-mono pt-3 border-t border-border">
        {s.model && (
          <>
            <span>{s.model}</span>
            <span>·</span>
          </>
        )}
        <span>{t("landing.sourceCount", { count: s.source_count })}</span>
        <div className="flex-1" />
        <button
          onClick={onDelete}
          disabled={pending}
          aria-label={t("me.delete")}
          className="w-7 h-7 -mr-1 rounded-md hover:bg-danger-soft inline-flex items-center justify-center text-ink-faint hover:text-[color:var(--danger)] disabled:opacity-40 transition-colors"
        >
          <Icon name="trash" size={13} />
        </button>
      </div>
    </Card>
  );
}
